import { useState } from 'react';
import ChatPanel from '../components/ChatPanel';
import ResumePreview from '../components/ResumePreview';
import TemplateSelector from '../components/TemplateSelector';

const defaultResume = {
  name: '', email: '', phone: '', location: '', linkedin: '',
  summary: '', experience: [], education: [], skills: [],
};

const defaultScore = {
  total: 0,
  breakdown: { contact: 0, summary: 0, experience: 0, education: 0, skills: 0 },
};

export default function Builder({ user, onLogout }) {
  const [resume, setResume] = useState(defaultResume);
  const [score, setScore] = useState(defaultScore);
  const [mobileTab, setMobileTab] = useState('chat');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const handleResumeUpdate = (newResume, newScore) => {
    setResume(newResume);
    if (newScore) setScore(newScore);
  };

  return (
    <div style={styles.root}>
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        /* Mobile (default): only one panel shows at a time, controlled by mobileTab via inline style */
        @media (max-width: 767px) {
          .top-bar { flex-wrap: wrap; padding: 8px 12px !important; gap: 8px !important; }
          .top-bar-right { width: 100%; justify-content: space-between; order: 3; }
          .logo-text { display: none; }
          .user-name { display: none; }
          .chat-panel { width: 100% !important; min-width: 0 !important; }
          .divider { display: none; }
        }

        /* Desktop: both panels always visible side by side, tab switcher hidden */
        @media (min-width: 768px) {
          .preview-panel { display: flex !important; }
          .chat-panel { display: flex !important; width: 400px !important; }
          .tab-switcher { display: none !important; }
        }
      `}</style>

      {!selectedTemplate && <TemplateSelector onSelect={setSelectedTemplate} />}

      {/* Top Bar */}
      <div className="top-bar" style={styles.topBar}>
        <div style={styles.logo}>
          <span style={styles.logoIcon}>✦</span>
          <span className="logo-text" style={styles.logoText}>AI ResumeBuilder</span>
        </div>

        {/* Mobile Tab Switcher - hidden on desktop */}
        <div className="tab-switcher" style={styles.tabSwitcher}>
          <button style={{ ...styles.tabBtn, ...(mobileTab === 'chat' ? styles.tabActive : {}) }} onClick={() => setMobileTab('chat')}>Chat</button>
          <button style={{ ...styles.tabBtn, ...(mobileTab === 'preview' ? styles.tabActive : {}) }} onClick={() => setMobileTab('preview')}>Preview</button>
        </div>

        <div className="top-bar-right" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {selectedTemplate && (
            <button onClick={() => setSelectedTemplate(null)} style={{
              background: 'transparent', border: '1px solid #333',
              color: '#a78bfa', padding: '5px 12px', borderRadius: '8px',
              fontSize: '12px', cursor: 'pointer', fontFamily: "'Inter', sans-serif",
            }}>🎨 {selectedTemplate.name}</button>
          )}
          <div style={styles.scoreBadge}>
            <span style={styles.scoreBadgeText}>Score: </span>
            <span style={{ ...styles.scoreBadgeValue, color: score.total >= 75 ? '#4ade80' : score.total >= 50 ? '#facc15' : '#f87171' }}>
              {score.total}/100
            </span>
          </div>
          <img src={user.photoURL} alt="avatar" style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0 }} />
          <span className="user-name" style={{ color: '#aaa', fontSize: '13px', fontFamily: "'Inter', sans-serif", whiteSpace: 'nowrap' }}>{user.displayName}</span>
          <button onClick={onLogout} style={{
            background: 'transparent', border: '1px solid #333',
            color: '#888', padding: '5px 12px', borderRadius: '8px',
            fontSize: '12px', cursor: 'pointer', fontFamily: "'Inter', sans-serif",
          }}>Logout</button>
        </div>
      </div>

      {/* Split Layout */}
      <div style={styles.body}>
        {/* Chat Panel */}
        <div
          className="no-scrollbar chat-panel"
          style={{
            ...styles.chatPanel,
            display: mobileTab === 'chat' ? 'flex' : 'none',
          }}
        >
          <ChatPanel onResumeUpdate={handleResumeUpdate} template={selectedTemplate} />
        </div>

        <div className="divider" style={styles.divider} />

        {/* Preview Panel - always visible on desktop, tab-controlled on mobile */}
        <div
          className="no-scrollbar preview-panel"
          style={{
            ...styles.previewPanel,
            display: mobileTab === 'preview' ? 'flex' : 'none',
          }}
        >
          <ResumePreview resume={resume} score={score} templateId={selectedTemplate?.id} />
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: { display: 'flex', flexDirection: 'column', height: '100vh', background: '#0a0a0a', overflow: 'hidden' },
  topBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 24px', borderBottom: '1px solid #1e1e1e',
    background: '#0f0f0f', flexShrink: 0, gap: '12px',
  },
  logo: { display: 'flex', alignItems: 'center', gap: '8px' },
  logoIcon: { color: '#a78bfa', fontSize: '18px' },
  logoText: { color: '#f0f0f0', fontWeight: '700', fontSize: '16px', fontFamily: "'Inter', sans-serif", letterSpacing: '0.5px' },
  tabSwitcher: { display: 'flex', background: '#1a1a1a', borderRadius: '8px', padding: '3px', gap: '2px' },
  tabBtn: { background: 'transparent', border: 'none', color: '#666', padding: '6px 16px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontFamily: "'Inter', sans-serif", transition: 'all 0.2s' },
  tabActive: { background: '#a78bfa', color: '#fff' },
  scoreBadge: { display: 'flex', alignItems: 'center', gap: '4px', background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '6px 12px' },
  scoreBadgeText: { color: '#666', fontSize: '12px', fontFamily: "'Inter', sans-serif" },
  scoreBadgeValue: { fontWeight: '700', fontSize: '13px', fontFamily: "'Inter', sans-serif" },
  body: { display: 'flex', flex: 1, overflow: 'hidden' },
  chatPanel: {
    width: '400px', minWidth: '320px', flexShrink: 0,
    flexDirection: 'column', overflow: 'hidden', height: '100%',
  },
  previewPanel: {
    flex: 1, flexDirection: 'column',
    overflow: 'auto', height: '100%',
    background: '#141414',
  },
  divider: { width: '1px', background: '#1e1e1e', flexShrink: 0 },
};
